import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db8hs9bk13ra8mig8s0g";

const VALUES = [
  {
    icon: "ri-emotion-happy-line",
    title: "快乐优先",
    desc: "我们相信，轻松和愉悦是最好的日常补给，每一条内容都要让人真心想笑。",
  },
  {
    icon: "ri-hearts-line",
    title: "真诚筛选",
    desc: "不制造焦虑，不堆砌低质内容，只把真正有趣的东西挑出来分享给你。",
  },
  {
    icon: "ri-group-line",
    title: "陪伴用户",
    desc: "从通勤到睡前，我们希望成为你随手打开的快乐补给站，简单、可靠、常在。",
  },
];

const CONTACTS = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "hello@xiuxiu.fun" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "羞羞网站" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@羞羞网站" },
];

const TEAM = [
  { name: "内容组", role: "选题与精选", desc: "每天泡在互联网里，只为找到最好笑的那一条。" },
  { name: "访谈组", role: "人物与故事", desc: "把有趣的人请到镜头前，聊聊快乐是怎么来的。" },
  { name: "产品组", role: "体验与设计", desc: "让每一次打开都简单顺滑，不浪费你的一秒钟。" },
];

export default function AboutPage() {
  useSEO({
    title: "关于我们 - 羞羞网站",
    description:
      "了解羞羞网站的品牌定位、团队理念与联系方式，我们是一个专注轻松娱乐内容的综合聚合平台。",
    keywords: "羞羞网站,关于我们,娱乐平台,轻松娱乐,团队介绍",
  });

  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("company_alt") ?? "").trim();
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() !== "") payload.append(key, value);
    });

    // 蜜罐命中：不发送任何请求，给出与成功一致的反馈
    if (honeypot) {
      setFormState("success");
      form.reset();
      return;
    }

    setFormState("submitting");
    setFormError("");
    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setFormState("success");
        form.reset();
      } else {
        setFormState("error");
        setFormError(isSpam ? "提交内容被判定为垃圾信息，请修改后重试。" : serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormState("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full bg-background-50">
      <Navbar />
      <main className="pt-16">
        <section className="w-full px-4 md:px-8 pt-14 pb-10 bg-background-100 border-b border-background-200">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
              <i className="ri-emotion-happy-line" />
              关于羞羞网站
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mt-5 leading-snug">
              让每一次放松，都来得简单一点
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mt-5 leading-relaxed">
              羞羞网站是一个综合娱乐内容聚合平台，专注于趣味视频、搞笑段子和轻松访谈。
              我们不追求宏大叙事，只想在你需要喘口气的时候，递上一点点简单的快乐。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-7">
                <span className="w-12 h-12 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className={`${v.icon} text-2xl`} />
                </span>
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mt-4">{v.title}</h3>
                <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <div className="mb-8">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
              我们的团队与理念
            </h2>
            <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
              一支小而专注的团队，分工不同，目标一致：让更多人在忙碌生活里，找到会心一笑的瞬间。
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TEAM.map((t) => (
              <div key={t.name} className="rounded-lg bg-background-50 border border-background-200 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold text-base text-foreground-950">{t.name}</h3>
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    {t.role}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                意见与反馈
              </h2>
              <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                有想看的选题、发现了好笑的段子，或者只是想跟我们聊两句？填一下下面的表单，
                我们会认真读每一条留言。
              </p>

              <div className="flex flex-col gap-3 mt-7">
                {CONTACTS.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => setToast(`已复制联系方式：${c.value}`)}
                    className="flex items-center gap-4 rounded-lg bg-background-100 border border-background-200 hover:border-primary-300 p-4 text-left transition-colors"
                  >
                    <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                      <i className={`${c.icon} text-xl`} />
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">{c.label}</span>
                      <span className="block text-sm font-medium text-foreground-950">{c.value}</span>
                    </span>
                    <i className="ri-file-copy-line text-foreground-400 ml-auto" />
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
              <form data-readdy-form id="feedback-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div>
                  <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-2">
                    你的称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你？"
                    className="w-full rounded-md bg-background-50 border border-background-300 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-2">
                    邮箱地址
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="方便我们回复你"
                    className="w-full rounded-md bg-background-50 border border-background-300 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-2">
                    想说的话
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="写下你的建议或想看的选题（500字以内）"
                    className="w-full rounded-md bg-background-50 border border-background-300 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field--alt"
                />

                <button
                  type="submit"
                  disabled={formState === "submitting"}
                  className="w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
                >
                  {formState === "submitting" ? "提交中..." : "提交反馈"}
                </button>

                {formState === "success" && (
                  <p className="text-sm text-secondary-700 bg-secondary-100 rounded-md px-4 py-3">
                    提交成功，感谢你的反馈！我们会尽快查看。
                  </p>
                )}
                {formState === "error" && (
                  <p className="text-sm text-primary-700 bg-primary-100 rounded-md px-4 py-3">
                    {formError}
                  </p>
                )}
                <p className="text-xs text-foreground-500">
                  提示：本表单为演示环境，提交内容会用于我们改进内容体验。
                </p>
              </form>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 pb-16">
          <div className="rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 px-6 md:px-12 py-10 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h2 className="font-heading font-bold text-2xl text-background-50">
                现在就去逛逛，找个乐子吧
              </h2>
              <p className="text-background-50/90 text-sm mt-2">
                上千条精选娱乐内容，随时打开，随时开心。
              </p>
            </div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 text-primary-600 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" />
              返回首页
            </Link>
          </div>
        </section>
      </main>
      <Footer />

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm">
          {toast}
        </div>
      )}
    </div>
  );
}